import supabase from "@/services/supabaseClient";

const LOCAL_STORAGE_PREFIX = "afrimed.settings.welcome";

const ROLE_FIELD_MAP = {
  doctor: {
    pending: "doctor_settings_welcome_pending",
    dismissedAt: "doctor_settings_welcome_dismissed_at",
  },
  patient: {
    pending: "patient_settings_welcome_pending",
    dismissedAt: "patient_settings_welcome_dismissed_at",
  },
};

function resolveRoleFields(role) {
  const normalizedRole = String(role || "").toLowerCase().trim();
  return ROLE_FIELD_MAP[normalizedRole] || ROLE_FIELD_MAP.patient;
}

function getStorageKey(userId, role) {
  return `${LOCAL_STORAGE_PREFIX}:${String(role || "").trim()}:${String(userId || "").trim()}`;
}

function readLocalState(userId, role) {
  if (typeof window === "undefined") return null;
  const key = getStorageKey(userId, role);
  if (!key) return null;

  try {
    const parsed = JSON.parse(window.localStorage.getItem(key) || "null");
    if (!parsed || typeof parsed !== "object") return null;
    return {
      pending: parsed.pending === true,
      dismissedAt: String(parsed.dismissedAt || "").trim(),
    };
  } catch {
    return null;
  }
}

function writeLocalState(userId, role, nextState) {
  if (typeof window === "undefined") return;
  const key = getStorageKey(userId, role);
  if (!key) return;

  try {
    window.localStorage.setItem(
      key,
      JSON.stringify({
        pending: nextState?.pending === true,
        dismissedAt: String(nextState?.dismissedAt || "").trim(),
      })
    );
  } catch {
    // Ignore storage failures; DB persistence still runs.
  }
}

export function getSettingsWelcomeState({ userId, role, profile } = {}) {
  const normalizedUserId = String(userId || "").trim();
  if (!normalizedUserId) {
    return { pending: false, dismissedAt: "" };
  }

  const fields = resolveRoleFields(role);
  const localState = readLocalState(normalizedUserId, role);
  const profilePending = profile?.[fields.pending] === true;
  const profileDismissedAt = String(profile?.[fields.dismissedAt] || "").trim();

  const pending =
    typeof localState?.pending === "boolean" ? localState.pending : profilePending;
  const dismissedAt =
    typeof localState?.dismissedAt === "string"
      ? localState.dismissedAt
      : profileDismissedAt;

  return {
    pending: !!pending && !dismissedAt,
    dismissedAt,
  };
}

export function shouldShowSettingsWelcome({ userId, role, profile } = {}) {
  const state = getSettingsWelcomeState({ userId, role, profile });
  return state.pending && !state.dismissedAt;
}

export async function markSettingsWelcomePending(userId, role) {
  const normalizedUserId = String(userId || "").trim();
  if (!normalizedUserId) return;

  const fields = resolveRoleFields(role);
  writeLocalState(normalizedUserId, role, { pending: true, dismissedAt: "" });

  try {
    await supabase.from("profiles").upsert(
      {
        user_id: normalizedUserId,
        [fields.pending]: true,
        [fields.dismissedAt]: null,
        updated_at: new Date().toISOString(),
      },
      { onConflict: "user_id" }
    );
  } catch {
    // Local storage already preserves the welcome state for this user/device.
  }
}

export async function dismissSettingsWelcome(userId, role) {
  const normalizedUserId = String(userId || "").trim();
  if (!normalizedUserId) return;

  const fields = resolveRoleFields(role);
  const dismissedAt = new Date().toISOString();
  writeLocalState(normalizedUserId, role, { pending: false, dismissedAt });

  try {
    await supabase.from("profiles").upsert(
      {
        user_id: normalizedUserId,
        [fields.pending]: false,
        [fields.dismissedAt]: dismissedAt,
        updated_at: dismissedAt,
      },
      { onConflict: "user_id" }
    );
  } catch {
    // Keep the local dismissal so the dialog never becomes a stuck record.
  }
}
